import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  cancelAtPeriodEnd,
  createCheckout,
  createPortal,
  getMySubscription,
  keepSubscription,
  listPlans,
  pauseSubscription,
  resumeSubscription,
  type CancelReason,
} from "@/lib/api/billing";

export const billingKeys = {
  subscription: (userId: string) => ["billing", "subscription", userId] as const,
  plans: ["billing", "plans"] as const,
};

export function useSubscription(userId: string | undefined) {
  return useQuery({
    queryKey: billingKeys.subscription(userId ?? ""),
    queryFn: () => getMySubscription(userId!),
    enabled: Boolean(userId),
  });
}

export function usePlans(enabled = true) {
  return useQuery({ queryKey: billingKeys.plans, queryFn: listPlans, enabled });
}

/** Resolves with the Checkout URL; the page redirects the browser there. */
export function useCheckout() {
  return useMutation({ mutationFn: createCheckout });
}

export function usePortal() {
  return useMutation({ mutationFn: createPortal });
}

export function useInvalidateSubscription() {
  const queryClient = useQueryClient();
  // The workspace's plan follows the subscription (copied by the server), so refresh it too.
  return () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: ["billing", "subscription"] }),
      queryClient.invalidateQueries({ queryKey: ["workspace"] }),
    ]);
}

/** Cancel at the period end, keep, pause or resume; the subscription and plan refresh after. */
export function useRetention() {
  const invalidate = useInvalidateSubscription();
  const options = { onSuccess: () => void invalidate() };
  return {
    cancel: useMutation({
      mutationFn: ({ reason, comment }: { reason: CancelReason; comment: string }) =>
        cancelAtPeriodEnd(reason, comment),
      ...options,
    }),
    keep: useMutation({ mutationFn: keepSubscription, ...options }),
    pause: useMutation({ mutationFn: pauseSubscription, ...options }),
    resume: useMutation({ mutationFn: resumeSubscription, ...options }),
  };
}
